import {
    ChangeDetectorRef, DestroyRef, Directive, HostListener, inject, Input, OnInit
} from "@angular/core";
import { MatCheckbox } from "@angular/material/checkbox";
import { ListDataSourceService } from "../services/list-data-source.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ProcessingDirective } from "../../application/directives/processing/processing.directive";
import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { LIST_STATE_SERVICE } from "../constants/tokens";
import { RowStateType } from "../enums/row-state-type";
import { ListUtil } from "../util/list-util";

/**
 * Directive that enhances checkboxes for use in list and table components.
 *
 * This directive connects MatCheckbox components to the ListDataSource selection model,
 * enabling features like:
 * - Row selection tracking
 * - Header checkbox for select/deselect all functionality
 * - Automatic state synchronization with the data source
 * - Disabled state management based on row state
 *
 * Usage:
 * ```html
 * <!-- For header checkbox (select all) -->
 * <mat-checkbox mgdListCheckbox [header]="true"></mat-checkbox>
 *
 * <!-- For row checkbox -->
 * <mat-checkbox mgdListCheckbox [row]="rowData"></mat-checkbox>
 * ```
 */
@Directive({
    selector: "[mgdListCheckbox]",
    hostDirectives: [
        {
            directive: ProcessingDirective,
            inputs: ["disableWhileProcessing"]
        }
    ]
})
export class ListCheckboxDirective implements OnInit {

    /**
     * Optional row input
     */
    @Input() row?: object;
    @Input({ transform: coerceBooleanProperty }) header?: boolean = false;

    private readonly _checkbox = inject(MatCheckbox, { self: true });
    private readonly _dataSource = inject(ListDataSourceService);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _processing = inject(ProcessingDirective, { self: true });
    private readonly _cdRef = inject(ChangeDetectorRef);
    private readonly _state = inject(LIST_STATE_SERVICE);

    /**
     * Stop the click event from propagating to the parent's component
     */
    @HostListener("click", ["$event"])
    onClick(event: PointerEvent): void {

        event.stopPropagation();
    }

    ngOnInit(): void {

        this._dataSource.selectedData.changed.pipe(
            // Unsubscribe when the component is destroyed
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((change) => {
            if (this.header) {
                if (this._dataSource.isAllSelected) {
                    this._checkbox.checked = true;
                    this._checkbox.indeterminate = false;
                }
                else if (this._dataSource.selectedData.selected.length > 0) {
                    this._checkbox.checked = false;
                    this._checkbox.indeterminate = true;
                }
                else {
                    this._checkbox.checked = false;
                    this._checkbox.indeterminate = false;
                }
            }
            else if (change.added.includes(ListUtil.unwrapRow<object>(this.row)) && !this._checkbox.checked) {
                this._checkbox.checked = true;

            }
            else if (change.removed.includes(ListUtil.unwrapRow<object>(this.row)) && this._checkbox.checked) {
                this._checkbox.checked = false;
            }
            this._cdRef.markForCheck();
        });

        this._checkbox.change.pipe(
            // Unsubscribe when the component is destroyed
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((change) => {
            if (this.header) {
                if (change.checked) {
                    this._dataSource.selectAll();
                }
                else {
                    this._dataSource.selectedData.clear();
                }
            }
            else if (this.row) {
                const rawRow = ListUtil.unwrapRow<object>(this.row);
                if (change.checked) {
                    this._dataSource.selectedData.select(rawRow);
                }
                else {
                    this._dataSource.selectedData.deselect(rawRow);
                }
            }
            this._cdRef.markForCheck();
        });

        if (this.header) {
            this._dataSource.data$.pipe(
                // Unsubscribe when the component is destroyed
                takeUntilDestroyed(this._destroyRef)
            ).subscribe(() => {
                this._processing.setSelfDisabled(this._dataSource.isEmpty);
                this._cdRef.markForCheck();
            });
        }

        if (this.row) {
            this._state.getRowState$(ListUtil.unwrapRow<object>(this.row)).pipe(
                // Unsubscribe when the component is destroyed
                takeUntilDestroyed(this._destroyRef)
            )
                .subscribe((state) => {
                    this._processing.setSelfDisabled(state?.type === RowStateType.Disabled);
                    this._cdRef.markForCheck();
                });
        }
    }
}
